'use client';

import React from 'react';
import Link from 'next/link';
import {
    Group,
    Text,
    Title,
    Stack,
    ThemeIcon,
    Anchor,
    Card,
    Divider,
    Button,
} from '@mantine/core';
import styles from './SiteOverviewPanel.module.scss';
import FuturisticStatCircle from './FuturisticStatCircle/FuturisticStatCircle';
import { usePathname } from 'next/navigation';
import { Locale } from '@/i18n-config';
import { primaryColor } from '@/src/styles/colors';

interface OverviewItem {
    id: string;
    title: string;
    description: string | { [key in Locale]: string };
    link: string;
    imageUrl: string;
}

interface SiteOverviewPanelProps {
    visitors: number;
    articles: {
        count: number;
        recent: OverviewItem[];
    };
    products: {
        count: number;
        recent: OverviewItem[];
    };
    estimations: {
        count: number;
        recent: OverviewItem[];
    };
}

const StatBlock = ({
    icon,
    label,
    value,
    color,
}: {
    icon: string; // L'URL de l'image
    label: string;
    value: number;
    color: string;
}) => (
    <div className={styles.statBlock}>
        <div className={styles.statIconWrapper}>
            <img src={icon} alt={label} style={{ width: '30px', height: '30px' }} />
        </div>
        <div className={styles.statText}>
            <Text size="sm" className={styles.statLabel}>
                {label}
            </Text>
            <Title order={3} className={styles.statValue}>
                {value ?? '-'}
            </Title>
        </div>
    </div>
);

const ListWidget = ({
    title,
    icon,
    items,
    color,
    link,
}: {
    title: string;
    icon: string; // L'URL de l'image
    items: OverviewItem[];
    color?: string;
    link: string;
}) => {
    const pathname = usePathname();
    const locale = pathname.split('/')[1] as Locale;
    return (
        <Card className={styles.listCard} withBorder radius="lg">
            <Group mb="xs">
                <Group >
                    <img src={icon} alt={title} style={{ width: '20px', height: '20px' }} />
                    <Title order={4} className={styles.listTitle}>
                        {title}
                    </Title>
                </Group>
                <Link href={link} passHref>
                    <Button variant="subtle" color={primaryColor}>
                        Voir plus
                    </Button>
                </Link>
            </Group>
            <Divider mb="sm" />
            <Stack gap="xs" className={styles.itemsList}>
                {items.length === 0 ? (
                    <Text size="sm" c="dimmed">
                        Aucun élément récent
                    </Text>
                ) : (
                    items.slice(0, 3).map((item) => (
                        <Card key={item.id} shadow="sm" p="sm" radius="md" className={styles.itemCard}>
                            <Link href={item.link}>
                                <div className={styles.itemImageWrapper}>
                                    <img src={item.imageUrl || '/default-image.jpg'} alt={item.title} className={styles.itemImage} />
                                </div>
                                <Text size="sm" className={styles.itemTitle}>
                                    {item.title}
                                </Text>
                                <Text size="xs" color="dimmed" className={styles.itemDescription}>
                                    {typeof item.description === 'object' && item.description !== null
                                        ? item.description[locale] || Object.values(item.description)[0]
                                        : item.description}
                                </Text>
                            </Link>
                        </Card>
                    ))
                )}
            </Stack>
        </Card>
    );
};

const SiteOverviewPanel: React.FC<SiteOverviewPanelProps> = ({
    visitors,
    articles,
    products,
    estimations,
}) => {
    return (
        <div className={styles.siteOverviewPanel}>
            {/* Section 1: Visitors & Metrics */}
            <div className={styles.visitorsSection}>
                <div className={styles.visitorsCircle}>
                    <FuturisticStatCircle
                        value={visitors ?? 0}
                        label="Nombre total de visiteurs"
                    />
                </div>
                <div className={styles.statsGrid}>
                    <StatBlock
                        icon="https://img.icons8.com/isometric-line/50/web-design.png"
                        label="Articles"
                        value={articles.count}
                        color={primaryColor}
                    />
                    <StatBlock
                        icon="https://img.icons8.com/isometric-line/50/shopping-cart.png"
                        label="Produits"
                        value={products.count}
                        color={primaryColor}
                    />
                    <StatBlock
                        icon="https://img.icons8.com/isometric-line/50/money-bag.png"
                        label="Devis"
                        value={estimations.count}
                        color={primaryColor}
                    />
                </div>
            </div>

            {/* Section 2: Recent Articles */}
            <div className={styles.section}>
                <ListWidget
                    title="Articles récents"
                    icon="https://img.icons8.com/isometric-line/50/web-design.png" // Remplacer par ton icône webp
                    items={articles.recent}
                    color={primaryColor}
                    link="/articles#recent"
                />
            </div>

            {/* Section 3: Recent Products */}
            <div className={styles.section}>
                <ListWidget
                    title="Produits récents"
                    icon="https://img.icons8.com/isometric-line/50/shopping-cart.png"
                    items={products.recent}
                    color='white'
                    link="/products#recent"
                />
            </div>

            {/* Section 4: Recent Estimations */}
            <div className={styles.section}>
                <ListWidget
                    title="Devis récents"
                    icon="https://img.icons8.com/isometric-line/50/money-bag.png"
                    items={estimations.recent}
                    color={primaryColor}
                    link="/estimations#recent"
                />
            </div>
        </div>
    );
};

export default SiteOverviewPanel;
